import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import FormRenderer from '../../../components/forms/FormRenderer';
import formService from '../../../services/formService';
import { supabase } from '../../../utils/supabase';
import CoverLetterTemplateSelector from './CoverLetterTemplateSelector';

// Only http/https URLs are allowed for portfolio links
const ALLOWED_URL_PROTOCOLS = ['http:', 'https:'];
const sanitizePortfolioUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  try {
    const parsed = new URL(url.trim());
    return ALLOWED_URL_PROTOCOLS.includes(parsed.protocol) ? url.trim() : '';
  } catch {
    return '';
  }
};

const ApplicationModal = ({ job, userProfile, onClose, onSubmit, asPage = false }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cover_letter: '',
    portfolio_links: [''],
    proposed_rate_min: userProfile?.hourly_rate || '',
    proposed_rate_max: '',
    availability_start: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [jobForms, setJobForms] = useState([]);
  const [formResponses, setFormResponses] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [hasApplied, setHasApplied] = useState(false);

  // Load forms attached to this job
  useEffect(() => {
    const loadJobForms = async () => {
      if (!job?.id) return;
      
      setLoadingForm(true);
      try {
        const result = await formService.getJobForms(job.id);
        if (result.success && result.data) {
          setJobForms(result.data);
        }
      } catch (error) {
        console.error('Error loading job forms:', error);
      } finally {
        setLoadingForm(false);
      }
    };

    loadJobForms();
  }, [job?.id]);

  // Check if user has already applied to this job
  useEffect(() => {
    const checkExistingApplication = async () => {
      if (!job?.id || !userProfile?.id) return;
      
      try {
        const { data: existingApplication, error: appError } = await supabase
          .from('job_applications')
          .select('id')
          .eq('job_id', job.id)
          .eq('applicant_id', userProfile.id)
          .maybeSingle();
        
        if (!appError && existingApplication) {
          setHasApplied(true);
        }
      } catch (err) {
        // Silently handle - error means no application found (expected behavior)
      }
    };

    checkExistingApplication();
  }, [job?.id, userProfile?.id]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePortfolioLinkChange = (index, value) => {
    const newLinks = [...formData.portfolio_links];
    newLinks[index] = value;
    setFormData(prev => ({
      ...prev,
      portfolio_links: newLinks
    }));
  };

  const addPortfolioLink = () => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: [...prev.portfolio_links, '']
    }));
  };

  const removePortfolioLink = (index) => {
    const newLinks = formData.portfolio_links.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      portfolio_links: newLinks.length === 0 ? [''] : newLinks
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.cover_letter.trim()) {
        throw new Error('Cover letter is required');
      }

      if (!formData.availability_start) {
        throw new Error('Availability start date is required');
      }

      // Validate form responses if job has forms attached
      if (jobForms.length > 0) {
        const form = jobForms[0];
        const errors = {};
        
        for (const question of form.questions || []) {
          if (question.required) {
            const response = formResponses[question.id];
            if (!response || !response.answer || 
               (Array.isArray(response.answer) && response.answer.length === 0) ||
               (typeof response.answer === 'string' && !response.answer.trim())) {
              errors[question.id] = 'This question is required';
            }
          }
        }

        if (Object.keys(errors).length > 0) {
          setFormErrors(errors);
          throw new Error('Please answer all required questions');
        }
      }

      // Clean up and sanitize portfolio links — strip empty, enforce http/https only
      const cleanPortfolioLinks = formData.portfolio_links
        .map(sanitizePortfolioUrl)
        .filter(link => link !== '');

      const applicationData = {
        ...formData,
        portfolio_links: cleanPortfolioLinks,
        proposed_rate_min: formData.proposed_rate_min ? parseFloat(formData.proposed_rate_min) : null,
        proposed_rate_max: formData.proposed_rate_max ? parseFloat(formData.proposed_rate_max) : null,
        formResponses: jobForms.length > 0 ? formResponses : null,
        formId: jobForms.length > 0 ? jobForms[0].id : null
      };

      await onSubmit(applicationData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Form content shared between modal and page versions
  const renderFormContent = () => (
    <>
      {/* Cover Letter */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Cover Letter *
        </label>
        <CoverLetterTemplateSelector
          userId={userProfile?.id}
          onSelectTemplate={(content) => handleInputChange('cover_letter', content)}
          currentCoverLetter={formData.cover_letter}
        />
        <textarea
          value={formData.cover_letter}
          onChange={(e) => handleInputChange('cover_letter', e.target.value)}
          placeholder="Tell the employer why you're the perfect fit for this role..."
          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
          rows={6}
          required
        />
        <p className="text-xs text-text-muted mt-1">
          Highlight your relevant experience and explain your interest in this position.
        </p>
      </div>

      {/* Portfolio Links */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Portfolio Links (Optional)
        </label>
        <div className="space-y-2">
          {formData.portfolio_links.map((link, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Input
                type="url"
                value={link}
                onChange={(e) => handlePortfolioLinkChange(index, e.target.value)}
                placeholder="https://your-portfolio.com"
                className="flex-1"
              />
              {formData.portfolio_links.length > 1 && (
                <button
                  type="button"
                  onClick={() => removePortfolioLink(index)}
                  className="p-2 text-error hover:bg-error-50 rounded-lg transition-colors"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
          ))}
          {formData.portfolio_links.length < 3 && (
            <button
              type="button"
              onClick={addPortfolioLink}
              className="flex items-center text-primary hover:text-primary-600 text-sm"
            >
              <Icon name="Plus" size={14} className="mr-1" />
              Add another link
            </button>
          )}
        </div>
      </div>

      {/* Proposed Rate and Availability */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Hourly Rate Range (per hour)
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.proposed_rate_min || ''}
              onChange={(e) => handleInputChange('proposed_rate_min', e.target.value)}
              placeholder="Min rate"
              className="flex-1"
            />
            <span className="text-text-secondary">-</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.proposed_rate_max || ''}
              onChange={(e) => handleInputChange('proposed_rate_max', e.target.value)}
              placeholder="Max rate"
              className="flex-1"
            />
          </div>
          {job?.min_salary && job?.max_salary && (
            <p className="text-xs text-text-muted mt-1">
              Job budget: ${job.min_salary} - ${job.max_salary} ${job?.salary_type === 'hourly' ? 'per hour' : 'annually'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Availability Start Date *
          </label>
          <Input
            type="date"
            value={formData.availability_start}
            onChange={(e) => handleInputChange('availability_start', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            required
          />
        </div>
      </div>

      {/* Job Forms (if attached) */}
      {loadingForm && (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="ml-3 text-text-secondary">Loading application form...</span>
        </div>
      )}
      
      {!loadingForm && jobForms.length > 0 && (
        <div className="pt-6 border-t border-border">
          <div className="mb-4">
            <h4 className="font-medium text-text-primary mb-1">
              <Icon name="FileText" size={16} className="inline mr-2" />
              Application Questions
            </h4>
            <p className="text-sm text-text-secondary">
              The employer requires additional information for this position
            </p>
          </div>
          <FormRenderer
            form={jobForms[0]}
            responses={formResponses}
            onChange={setFormResponses}
            errors={formErrors}
          />
        </div>
      )}

      {/* Profile Summary */}
      <div className="bg-secondary-50 rounded-lg p-4">
        <h4 className="font-medium text-text-primary mb-2">Your Profile Summary</h4>
        <div className="text-sm text-text-secondary space-y-1">
          <p><strong>Name:</strong> {userProfile?.full_name}</p>
          <p><strong>Experience:</strong> {userProfile?.experience_years || 0} years</p>
          {userProfile?.skills && userProfile.skills.length > 0 && (
            <div>
              <strong>Skills:</strong>
              <div className="flex flex-wrap gap-1 mt-1">
                {userProfile.skills.slice(0, 5).map((skill, index) => {
                  const skillName = typeof skill === 'string' ? skill : 
                                   (skill?.name || skill?.skill || 'Unknown Skill');
                  return (
                    <span
                      key={index}
                      className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded"
                    >
                      {skillName}
                    </span>
                  );
                })}
                {userProfile.skills.length > 5 && (
                  <span className="text-xs text-text-muted">
                    +{userProfile.skills.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          iconName={loading ? "Loader2" : "Send"}
        >
          {loading ? 'Submitting...' : 'Submit Application'}
        </Button>
      </div>
    </>
  );

  // Render as page content (no modal wrapper)
  if (asPage) {
    // Show already applied message if applicable
    if (hasApplied) {
      return (
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="bg-surface rounded-lg shadow-sm border border-border p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center">
                <Icon name="Check" size={32} className="text-success" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-text-primary mb-3">Already Applied</h2>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">
              You have already submitted an application for this position. The employer will review your application and contact you if they're interested in moving forward.
            </p>
            <div className="flex justify-center space-x-4">
              <Button
                variant="secondary"
                onClick={onClose}
              >
                Back to Job Details
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/job-search')}
              >
                Browse More Jobs
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-surface rounded-lg shadow-sm border border-border p-6">
          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={16} className="text-error mr-2" />
                <p className="text-error text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {renderFormContent()}
          </form>
        </div>
      </div>
    );
  }

  // Render as modal
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-surface shadow-xl rounded-lg">
          {/* Show already applied message if applicable */}
          {hasApplied ? (
            <div className="text-center py-8">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center">
                  <Icon name="Check" size={32} className="text-success" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                Already Applied
              </h3>
              <p className="text-text-secondary mb-6 max-w-sm mx-auto">
                You have already submitted an application for this position. The employer will review your application and contact you if interested.
              </p>
              <div className="flex justify-center space-x-3">
                <Button
                  variant="secondary"
                  onClick={onClose}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/job-search')}
                >
                  Browse More Jobs
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-text-primary">
                    Apply for {job?.title}
                  </h3>
                  <p className="text-text-secondary">
                    {job?.company_name || job?.employer?.company_name || job?.employer?.full_name || 'Unknown Company'} • {job?.location}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <Icon name="X" size={20} className="text-text-muted" />
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
                  <div className="flex items-center">
                    <Icon name="AlertCircle" size={16} className="text-error mr-2" />
                    <p className="text-error text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {renderFormContent()}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;
